import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Stethoscope, User } from 'lucide-react';
import NivaLogo from '../components/NivaLogo';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ROUTES } from '../constants/routes';
import { isTutorialCompleted } from '../utils/tutorialStorage';
import { notifyLogin } from '../services/loginAlertService';
import { apiPost } from '../config/api';
import { isPublicProductionHost } from '../utils/publicHost';

function AccessCodeNameLogin() {
  const navigate = useNavigate();
  const { login, nurseName } = useAuth();
  const { t } = useLanguage();
  const [name, setName] = useState(() => nurseName || '');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const result = login(name);
    if (result.ok) {
      const trimmed = name.trim();
      notifyLogin({ nurseName: trimmed }).catch(() => {});
      navigate(isTutorialCompleted() ? ROUTES.PATIENTS : ROUTES.ONBOARDING);
    } else {
      setError(t(result.error));
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-b from-sky-50 via-white to-primary-light px-4">
      <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-primary-mid opacity-60 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-primary-ring opacity-50 blur-3xl" />

      <div className="relative w-full max-w-[380px] rounded-2xl bg-surface p-8 shadow-card">
        <NivaLogo className="mb-8" />
        <h1 className="mb-6 text-center text-[28px] font-bold text-slate-900">{t('welcomeBack')}</h1>

        {error && (
          <div className="mb-4 rounded-xl bg-critical-light px-4 py-3 text-sm text-critical-dark">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <User className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('yourName')}
              className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              autoComplete="name"
            />
          </div>
          <button type="submit" className="btn-primary">
            {t('signIn')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function LoginScreen() {
  const navigate = useNavigate();
  const { loginWithToken, nurseName } = useAuth();
  const { t, language } = useLanguage();
  const [mode, setMode] = useState('chooser'); // chooser | hospital
  const [username, setUsername] = useState(() => nurseName || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  if (isPublicProductionHost()) {
    return <AccessCodeNameLogin />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const data = await apiPost('/api/nurse/login', { username: username.trim(), password });
      const result = loginWithToken(data.token, data.nurse?.name || username.trim(), {
        mustChangePassword: data.must_change_password,
        termsAccepted: data.terms_accepted ?? data.nurse?.terms_accepted,
        accountType: 'hospital',
      });
      if (result.ok && !result.mustChangePassword) {
        notifyLogin({ nurseName: data.nurse?.name || username.trim() }).catch(() => {});
        navigate(isTutorialCompleted() ? ROUTES.PATIENTS : ROUTES.ONBOARDING);
      }
    } catch (err) {
      setError(err.message || t('enterName'));
    }
  };

  if (mode === 'chooser') {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-b from-sky-50 via-white to-primary-light px-4">
        <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-primary-mid opacity-60 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-primary-ring opacity-50 blur-3xl" />

        <div className="relative w-full max-w-[380px] rounded-2xl bg-surface p-8 shadow-card">
          <NivaLogo className="mb-8" />
          <h1 className="mb-6 text-center text-[28px] font-bold text-slate-900">{t('welcomeBack')}</h1>

          <div className="space-y-3">
            <button
              type="button"
              onClick={() => navigate(ROUTES.DOCTOR_SIGNUP)}
              className="flex w-full items-start gap-3 rounded-xl border border-primary/20 bg-primary-light/40 px-4 py-4 text-left transition hover:bg-primary-light"
            >
              <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-white">
                <Stethoscope className="h-5 w-5" />
              </div>
              <div>
                <p className="text-base font-bold text-slate-900">
                  {language === 'ar' ? 'أنا طبيب' : 'I am a Doctor'}
                </p>
                <p className="mt-0.5 text-sm text-gray-500">
                  {language === 'ar' ? 'عيادة خاصة — خدمة ذاتية' : 'Private clinic — self-serve'}
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setMode('hospital')}
              className="flex w-full items-start gap-3 rounded-xl border border-gray-200 bg-white px-4 py-4 text-left transition hover:bg-gray-50"
            >
              <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-700">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <p className="text-base font-bold text-slate-900">
                  {language === 'ar' ? 'عندي كود مستشفى' : 'I have a hospital code'}
                </p>
                <p className="mt-0.5 text-sm text-gray-500">
                  {language === 'ar' ? 'انضم لفريق مستشفاك' : 'Join your hospital team'}
                </p>
              </div>
            </button>
          </div>

          <p className="mt-4 text-center text-sm text-gray-400">
            <button
              type="button"
              onClick={() => navigate(ROUTES.DOCTOR_LOGIN)}
              className="font-medium text-primary"
            >
              {language === 'ar' ? 'لدي حساب طبيب' : 'I already have a doctor account'}
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-b from-sky-50 via-white to-primary-light px-4">
      <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-primary-mid opacity-60 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-primary-ring opacity-50 blur-3xl" />

      <div className="relative w-full max-w-[380px] rounded-2xl bg-surface p-8 shadow-card">
        <NivaLogo className="mb-8" />
        <h1 className="mb-6 text-center text-[28px] font-bold text-slate-900">{t('welcomeBack')}</h1>

        {error && (
          <div className="mb-4 rounded-xl bg-critical-light px-4 py-3 text-sm text-critical-dark">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <User className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              autoComplete="username"
            />
          </div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full rounded-xl border border-gray-200 py-3 px-4 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            autoComplete="current-password"
          />
          <button type="submit" className="btn-primary">
            {t('signIn')}
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMode('chooser')}
          className="mt-4 w-full text-center text-sm text-gray-500"
        >
          {t('doctorBackToEntry')}
        </button>
      </div>
    </div>
  );
}
